import React, { useState } from "react";
import WebcamCapture from './WebcamCapture';
import jsQr from 'jsqr';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

const QRScanner = () => {
    const [qrCode, setQrCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchingPackage, setSearchingPackage] = useState(false);
    
    const navigate = useNavigate(); 

    const handleScan = async (imageSrc) => {
        if (imageSrc) {
            const image = new Image();
            image.src = imageSrc;
            image.onload = async () => {
                const canvas = document.createElement("canvas");
                canvas.width = image.width;
                canvas.height = image.height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

                // Get image data with correct parameters
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const code = jsQr(imageData.data, imageData.width, imageData.height, { inversionAttempts: "dontInvert" });

                if (code) {
                    const scannedData = code.data;
                    setQrCode(scannedData); // Store the decoded data string
                    console.log("Scanned QR code:", scannedData);
                    
                    try {
                        setLoading(true);
                        
                        // Check if the scanned data contains our domain URL
                        if (scannedData.includes('pacepaktt.com/package/')) {
                            // Extract package ID from the URL
                            const packageId = extractPackageIdFromUrl(scannedData);
                            
                            if (packageId) {
                                console.log("Extracted package ID:", packageId);
                                // Navigate to the package details page
                                navigate(`/package/${packageId}`);
                            } else {
                                setError("Invalid package URL format");
                                setLoading(false);
                            }
                        } 
                        // Check if it's a relative path (starts with /)
                        else if (scannedData.startsWith('/package/')) {
                            // It's already a relative path, navigate directly
                            navigate(scannedData);
                        }
                        // Check if it's just a package ID (no slashes or protocol)
                        else if (isValidPackageId(scannedData)) {
                            // Assume it's a package ID, navigate directly
                            navigate(`/package/${scannedData}`);
                        }
                        // Fallback: treat as tracking number and search database
                        else {
                            await searchByTrackingNumber(scannedData);
                        }
                    } catch (err) {
                        console.error("Error processing QR code:", err);
                        setError("Error processing QR code: " + err.message);
                        setLoading(false);
                    }
                } else {
                    setError("No QR code found in the image. Please try again.");
                }
            }
        }
    }

    // Extract package ID from URL
    const extractPackageIdFromUrl = (url) => {
        try {
            // Handle both full URLs and relative paths
            let urlToParse = url;
            
            // If it's a full URL, create a URL object
            if (url.startsWith('http')) {
                const urlObj = new URL(url);
                urlToParse = urlObj.pathname;
            }
            
            // Extract package ID from path like "/package/packageId" or "pacepaktt.com/package/packageId"
            const packageMatch = urlToParse.match(/\/package\/([a-zA-Z0-9\-_]+)/);
            
            if (packageMatch && packageMatch[1]) {
                return packageMatch[1];
            }
            
            return null;
        } catch (error) {
            console.error("Error extracting package ID:", error);
            return null;
        }
    };

    // Check if string looks like a valid package ID (alphanumeric with possible hyphens/underscores)
    const isValidPackageId = (str) => {
        // Package IDs are typically alphanumeric strings without spaces or special chars (except - and _)
        const packageIdRegex = /^[a-zA-Z0-9\-_]{10,}$/; // At least 10 characters
        return packageIdRegex.test(str) && !str.includes('/') && !str.includes('.');
    };

    // Search for package by tracking number (fallback method)
    const searchByTrackingNumber = async (trackingNumber) => {
        try {
            setSearchingPackage(true);
            
            const packagesRef = collection(db, 'packages');
            const q = query(packagesRef, where("trackingNumber", "==", trackingNumber));
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
                // Found a matching package, navigate to its details page
                const packageDoc = querySnapshot.docs[0];
                navigate(`/package/${packageDoc.id}`);
            } else {
                setError(`No package found with tracking number: ${trackingNumber}`);
                setSearchingPackage(false);
                setLoading(false);
            }
        } catch (err) {
            console.error("Error searching for package:", err);
            setError("Error searching for package: " + err.message);
            setSearchingPackage(false);
            setLoading(false);
        }
    };

    // Reset the scanner to try again
    const resetScanner = () => {
        setQrCode("");
        setError(null);
        setLoading(false);
        setSearchingPackage(false);
    };

    return (
        <div className="w-full flex flex-col items-center p-4">
            <h1 className="text-xl font-bold mb-4">QR Code Scanner</h1>
            <p className="text-sm text-gray-600 mb-4 text-center">
                Scan a package QR code to view its details.<br/>
                <span className="text-xs text-gray-500">
                    Supports URLs, package IDs, and tracking numbers
                </span>
            </p>
            
            {!qrCode && (
                <WebcamCapture onScan={handleScan} />
            )}
            
            {loading && (
                <div className="mt-4 flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mb-2"></div>
                    <p className="text-sm text-gray-600">
                        {searchingPackage ? "Searching for package..." : "Processing QR code..."}
                    </p>
                </div>
            )}
            
            {error && (
                <div className="mt-4 p-4 w-full max-w-md bg-red-100 text-red-700 rounded shadow-md">
                    <p className="text-center font-medium mb-2">Scan Failed</p>
                    <p className="text-sm text-center">{error}</p>
                    <button 
                        onClick={resetScanner}
                        className="mt-3 w-full py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            )}
            
            {qrCode && !loading && !error && (
                <div className="mt-4 p-4 w-full max-w-md bg-green-100 text-green-700 rounded shadow-md">
                    <h2 className="text-lg font-semibold mb-2 text-center">✓ QR Code Scanned</h2>
                    <div className="bg-white p-2 rounded border">
                        <p className="text-xs text-gray-500 mb-1">Scanned data:</p>
                        <p className="break-all font-mono text-sm">{qrCode}</p>
                    </div>
                    <p className="text-sm text-center mt-2">
                        Redirecting to package details...
                    </p>
                    <button 
                        onClick={resetScanner}
                        className="mt-3 w-full py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                    >
                        Scan Another
                    </button>
                </div>
            )}
            
            {/* Instructions */}
            <div className="mt-6 max-w-md">
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-800 text-center">
                        <strong>Tip:</strong> Point your camera at the QR code and wait for it to focus. 
                        The scanner will automatically detect and process the code.
                    </p>
                </div>
            </div>
            
            <div className="mt-4">
                <button
                    onClick={() => navigate(-1)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 flex items-center transition-colors"
                >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Dashboard
                </button>
            </div>
        </div>
    );
}

export default QRScanner;